import { TopBar, BottomNav } from "../hud/Hud.js";
import { SceneHost } from "../scene/SceneHost.js";
import { Settings } from "./Settings.js";
import { BaseView } from "../base/BaseView.js";
import { UnitsView } from "../units/UnitsView.js";
import { RevealOverlay } from "../units/Workshop.js";
import { ResultsOverlay } from "../battle/Results.js";
import { BattleView } from "../battle/BattleView.js";
import { ArenaView } from "../arena/ArenaView.js";
import { ExpeditionsView } from "../expeditions/ExpeditionsView.js";
import { WorldView } from "../world/WorldView.js";
import { SocialHub, useSocialPushes } from "../social/SocialHub.js";
import { ProfileSheet } from "../social/ProfileSheet.js";
import { StoreHub, useStorePushes } from "../store/StoreHub.js";
import { EventsHub, useEventPushes } from "../events/EventsHub.js";
import { TodayStrip } from "../events/TodayStrip.js";
import { Opening } from "../tutorial/Opening.js";
import { useSession } from "../state/session.js";
import { InventoryView } from "../inventory/InventoryView.js";
import { AllianceHub } from "../alliance/AllianceHub.js";
import { VisitView } from "../alliance/VisitView.js";
import { useAlliancePushes } from "../alliance/pushes.js";
import { AdminView } from "../admin/AdminView.js";
import { useState } from "react";
import { GlobalTicker } from "../social/GlobalTicker.js";
import { BattleJournal } from "../battle/BattleJournal.js";
import { OperationsView } from "../world/OperationsView.js";
import { NexusView } from "../nexus/NexusView.js";
import "../ui/tactile.css";
import "../world/operations.css";
import "../world/reference-ui.css";

/** The in-game shell: 3D scene underneath, HUD on top, route panels in between. */
export function Shell({ route }: { route: string }) {
  const recovery = useSession((state) => state.recoveryVersion);
  const accountId = useSession(state => state.account?.id);
  const [tickerVisible, setTickerVisible] = useState(false);
  useSocialPushes();
  useStorePushes();
  useEventPushes();
  useAlliancePushes();
  let panel: React.ReactNode = null;
  if (route.startsWith("/app/settings")) panel = <Settings />;
  else if (route.startsWith("/app/reports")) panel = <BattleJournal key={accountId} reportId={route.split('/')[3]} />;
  else if (route.startsWith("/app/nexus")) panel = <NexusView key={accountId} />;
  else if (route.startsWith("/app/operations")) panel = <OperationsView key={`${accountId}:${route}`} initialTab={route.endsWith("/chests")?"chests":route.endsWith("/draw")?"draw":"hunt"} />;
  else if (route.startsWith("/app/inventory")) panel = <InventoryView />;
  else if (route.startsWith("/app/admin")) panel = <AdminView />;
  else if (route.startsWith("/app/units")) panel = <UnitsView route={route} />;
  else if (route.startsWith("/app/arena")) panel = <ArenaView />;
  else if (route.startsWith("/app/expeditions")) panel = <ExpeditionsView />;
  else if (route.startsWith("/app/store")) panel = <StoreHub route={route} />;
  else if (route.startsWith("/app/events")) panel = <EventsHub route={route} />;
  else if (route.startsWith("/app/chat")) panel = <SocialHub route={route} />;
  else if (route.startsWith("/app/alliance")) panel = <AllianceHub route={route} />;
  else if (route.startsWith("/app/visit/")) panel = <VisitView key={route} accountId={decodeURIComponent(route.split("/")[3] ?? "")} />;

  const battleId = route.startsWith("/app/battle/") ? route.slice("/app/battle/".length) : null;
  const onMap = route.startsWith("/app/map");
  return (
    <div className="game-shell" data-game-route={route} style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
      <SceneHost active={route === "/app" || battleId !== null || onMap} />
      <BaseView active={route === "/app"} hidden={battleId !== null || onMap} />
      <WorldView key={recovery} active={onMap} />
      {battleId ? <BattleView key={`${battleId}:${recovery}`} battleId={battleId} /> : null}
      <TopBar />
      {route === "/app" ? <TodayStrip /> : null}
      <main className="shell-route-panels" style={{ position: "absolute", inset: 0, paddingTop: "calc(var(--safe-top) + 108px)", paddingBottom: `calc(var(--safe-bottom) + ${tickerVisible ? 144 : 84}px)`, pointerEvents: "none" }}>
        <div key={recovery} className="shell-route-scroll" style={{ position: "relative", height: "100%", overflowY: "auto", pointerEvents: panel ? "auto" : "none" }}>{panel}</div>
      </main>
      <BottomNav route={route} />
      <GlobalTicker key={accountId} route={route} onVisibilityChange={setTickerVisible} />
      <RevealOverlay />
      <ResultsOverlay />
      <ProfileSheet />
      <Opening route={route} />
    </div>
  );
}
